<!--
  Action dialog "Berechnete Vermittler neu ermitteln" on ONE cp.ProvisionContract event row
  (requirement §3.6, FD-7, decision D-5).

  IMPORTANT: This file contains NO form HTML. The mandatory reason is rendered by the transient
  form widget built from the MvsFormDto in the TS (repo precedent wf-process-start.component.html,
  identical to the sibling action dialogs of this module). Only the dialog frame, the mandatory
  preview and the action buttons live here.

  The preview renders THREE things, and the middle one is the honest part: what would be ADDED,
  which existing rows have become obsolete AND ARE DELIBERATELY LEFT STANDING (D-7/AB-8), and — when
  the server refuses — why. On a refusal the confirm button is unavailable, not merely failing on
  submit.

  All icons are Font Awesome (house standard UX7), never PrimeIcons.
-->
<p-dialog
        header="Berechnete Vermittler neu ermitteln"
        [visible]="visible"
        (visibleChange)="handleVisibleChange($event)"
        (onShow)="handleShow()"
        [modal]="true"
        [draggable]="false"
        [resizable]="false"
        [style]="{width: '52rem'}"
        [breakpoints]="{'960px': '90vw'}"
        styleClass="cp-provision-calculated-agent-reset-dialog">

    <!-- Context: which event row the release applies to. The scope is ONE row per call (§3.6 a). -->
    <div class="flex flex-wrap align-items-center gap-2 mb-3">
        <span class="text-color-secondary text-sm">Ereigniszeile</span>
        <span class="font-medium">{{ provisionContractLabel ?? ('#' + provisionContractId) }}</span>
    </div>

    <!-- The dialog opens with the action available (optimistic module pattern, see the class
         TSDoc); this banner appears only after the SERVER refused with a 403. -->
    @if (!canAgentChange) {
        <p-message severity="warn" styleClass="w-full mb-3">
            <span>
                Der Server hat die Freigabe verweigert: Ihnen fehlt das Recht
                <strong>cp_agentChange</strong>. Die Vorschau kann angesehen, die Freigabe aber
                nicht abgeschickt werden.
            </span>
        </p-message>
    }

    <!-- What the act does — stated before the operator reads any list, because "additiv" is the
         one property everything below depends on. -->
    <p-message severity="info" styleClass="w-full mb-3">
        <span>
            <i class="fa-regular fa-circle-info mr-2"></i>
            Die Freigabe hebt nur die Einfrierung dieser einen Ereigniszeile auf. Der nächste
            Berechnungslauf <strong>ergänzt</strong> fehlende berechnete Vermittler —
            <strong>bestehende Sätze werden nicht geändert, nicht entfernt und nicht
            storniert</strong>. Es wird nichts gebucht.
        </span>
    </p-message>

    <!-- Mandatory preview (§3.6 c) -->
    <div class="surface-100 border-round p-3">

        <div class="font-semibold mb-2">Vorschau</div>

        @if (previewState == 'loading') {

            <p-skeleton height="8rem"></p-skeleton>

        } @else if (previewState == 'failed') {

            <p-message severity="error" styleClass="w-full">
                <span>{{ failureMessage }}</span>
            </p-message>

        } @else if (previewState == 'loaded') {

            <!-- (1) Refusal reasons — rendered verbatim in the backend's own wording. -->
            @if (blocked) {
                <p-message severity="error" styleClass="w-full mb-3">
                    <div class="flex flex-column gap-2">
                        <span class="font-semibold">
                            Diese Ereigniszeile kann derzeit nicht freigegeben werden.
                        </span>
                        @if (blockedBy.length) {
                            <ul class="m-0 pl-4">
                                @for (reason of blockedBy; track reason) {
                                    <li>{{ reason }}</li>
                                }
                            </ul>
                        } @else {
                            <span>
                                Der Server hat die Freigabe abgelehnt, ohne einen Grund zu nennen.
                            </span>
                        }
                    </div>
                </p-message>
            }

            <!-- (2) Already released — the reset stays allowed but changes nothing (§3.6 e). -->
            @if (alreadyReleased && !blocked) {
                <p-message severity="info" styleClass="w-full mb-3">
                    <span>
                        Diese Ereigniszeile ist für berechnete Vermittler bereits freigegeben. Eine
                        erneute Freigabe ändert nichts.
                    </span>
                </p-message>
            }

            <!-- (3) What would be ADDED -->
            <div class="mb-3">

                <div class="flex align-items-center gap-2 mb-2">
                    <i class="fa-regular fa-circle-plus text-green-600"></i>
                    <span class="font-medium">Diese Regeln würden greifen</span>
                    <p-tag [value]="wouldFire.length.toString()" severity="secondary"></p-tag>
                </div>

                @if (!wouldFire.length) {
                    <p-message severity="info" styleClass="w-full">
                        <span>
                            Nach aktueller Konfiguration würde diese Ereigniszeile keinen berechneten
                            Vermittler erhalten. Die Freigabe bleibt möglich, hätte aber keine
                            Wirkung.
                        </span>
                    </p-message>
                } @else {
                    <div class="cp-reset-table-scroll">
                        <p-table [value]="wouldFire" styleClass="p-datatable-sm" [scrollable]="false">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Regel</th>
                                    <th>Zielkomponente</th>
                                    <th>Ziel-Vermittler</th>
                                    <th class="text-right">Priorität</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-rule>
                                <tr>
                                    <td>
                                        <div class="flex flex-column">
                                            <span class="font-medium">{{ rule.name ?? rule.alias }}</span>
                                            @if (rule.alias && rule.name) {
                                                <span class="text-color-secondary text-sm">{{ rule.alias }}</span>
                                            }
                                        </div>
                                    </td>
                                    <td>{{ rule.targetProvisionTypeComponentName }}</td>
                                    <td>{{ rule.targetProvisionAgentName }}</td>
                                    <td class="text-right">{{ rule.orderPriority }}</td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>

                    <span class="text-color-secondary text-sm">
                        Bereits vorhandene Kombinationen aus Zielkomponente und Ziel-Vermittler
                        werden vom Lauf übersprungen — es entstehen nur wirklich neue Sätze.
                    </span>
                }

            </div>

            <!-- (4) Obsolete rows — DISPLAY ONLY. The label says so before the table, the hint
                 says so after it, and a confirmation says it once more on submit. -->
            @if (obsoleteRows.length) {
                <div>

                    <div class="flex align-items-center gap-2 mb-2 flex-wrap">
                        <i class="fa-regular fa-box-archive text-color-secondary"></i>
                        <span class="font-medium">Obsolete berechnete Sätze</span>
                        <p-tag [value]="obsoleteRows.length.toString()" severity="secondary"></p-tag>
                        <p-tag value="Werden NICHT entfernt" severity="warn"></p-tag>
                    </div>

                    <div class="cp-reset-table-scroll">
                        <p-table [value]="obsoleteRows" styleClass="p-datatable-sm" [scrollable]="false">
                            <ng-template pTemplate="header">
                                <tr>
                                    <th>Satz</th>
                                    <th>Erzeugende Regel</th>
                                    <th class="text-right">Betrag</th>
                                </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-row>
                                <tr>
                                    <td>#{{ row.provisionContractAgentId }}</td>
                                    <td>
                                        {{ row.provisionTypeCalculatedAgentAlias ?? 'Unbekannt (vor Einführung der Regeln erzeugt)' }}
                                    </td>
                                    <td class="text-right">
                                        {{ row.provisionAmount != null ? (row.provisionAmount | currency:'EUR':'symbol':'1.2-2') : '–' }}
                                    </td>
                                </tr>
                            </ng-template>
                        </p-table>
                    </div>

                    <p-message severity="warn" styleClass="w-full mt-2">
                        <span>
                            <i class="fa-regular fa-triangle-exclamation mr-2"></i>
                            Diese Sätze würde die aktuelle Konfiguration nicht mehr erzeugen. Die
                            Freigabe lässt sie <strong>unverändert stehen</strong> — sie werden
                            weder gelöscht noch storniert, weil ein entfernter Satz die
                            Rücklagendeckung des Vermittlers rückwirkend verändern würde. Das
                            Bereinigen bleibt der bestehende, separate Weg: Betrag auf 0,00
                            überschreiben bzw. der Stornopfad.
                        </span>
                    </p-message>

                </div>
            }

        }

    </div>

    <!-- Capture: metadata-driven form widget (the mandatory reason) -->
    <div class="mt-3">
        @if (formWidget) {
            <mvs-widget
                    [widgetData]="formWidget"
                    (onGenericEvent)="handleGenericEvent($event)">
            </mvs-widget>
        }
    </div>

    @if (failureMessage && previewState != 'failed') {
        <p-message severity="error" [text]="failureMessage" styleClass="w-full mt-3"></p-message>
    }

    <ng-template pTemplate="footer">
        <p-button
                label="Abbrechen"
                icon="fa-regular fa-xmark"
                severity="secondary"
                [text]="true"
                (onClick)="handleClose()">
        </p-button>

        <p-button
                label="Neuermittlung freigeben"
                icon="fa-regular fa-arrows-rotate"
                severity="primary"
                [loading]="busy"
                [disabled]="!submittable"
                [pTooltip]="submitTooltip"
                tooltipPosition="top"
                (onClick)="handleReset()">
        </p-button>
    </ng-template>

</p-dialog>
